import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, X as XIcon, Award, Clock, Globe, Calendar } from "lucide-react";
import { useFetch } from "../lib/api";
import { SectionLabel } from "../components/shared";

export default function FrameworkDetail() {
  const { slug } = useParams();
  const { data: frameworks, loading } = useFetch("/frameworks");
  const f = (frameworks || []).find((x) => x.slug === slug);
  if (loading) return <main className="min-h-[50vh] flex items-center justify-center"><div className="w-9 h-9 rounded-full border-[3px] border-[#3F4CE0] border-t-transparent animate-spin" /></main>;
  if (!f) return <main className="max-w-3xl mx-auto px-5 py-24 text-center"><p className="text-[17px]">Framework not found.</p><Link to="/library" className="text-[#3F4CE0] underline">Back to library</Link></main>;
  const related = (frameworks || []).filter((x) => x.category === f.category && x.slug !== f.slug).slice(0, 3);
  return (
    <main className="max-w-[1100px] mx-auto px-5 lg:px-8 py-12" data-testid="framework-detail-page">
      <Link to="/library" className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#1A1914]/60 hover:text-[#3F4CE0] rp-btn"><ArrowLeft size={14} /> Framework Library</Link>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="font-mono-label text-[10px] px-2.5 py-1 rounded-full bg-[#F3EEE4] border border-[#E7E0D2] text-[#1A1914]/55">{f.category}</span>
        {f.certification && <span className="inline-flex items-center gap-1 font-mono-label text-[10px] px-2.5 py-1 rounded-full bg-[#12B76A]/10 border border-[#12B76A]/25 text-[#0E9355]"><Award size={11} /> Certifiable</span>}
      </div>
      <h1 className="mt-4 font-display font-bold text-[40px] lg:text-[56px] tracking-tight leading-[1.02]">{f.name}</h1>
      <p className="mt-2 text-[18px] text-[#1A1914]/55">{f.full}</p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Globe, label: "Origin", value: f.origin },
          { icon: Calendar, label: "Current edition", value: String(f.year) },
          { icon: Clock, label: "Time to implement", value: f.timeToImplement },
          { icon: Award, label: "Complexity", value: `${f.complexity} / 5` }
        ].map((m) => (
          <div key={m.label} className="bg-white border border-[#E7E0D2] rounded-xl p-4">
            <p className="font-mono-label text-[9.5px] text-[#1A1914]/40 flex items-center gap-1.5"><m.icon size={11} /> {m.label}</p>
            <p className="mt-1.5 text-[14.5px] font-semibold">{m.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <SectionLabel>Overview</SectionLabel>
            <p className="text-[15.5px] leading-relaxed text-[#1A1914]/75">{f.description}</p>
            <p className="mt-4 text-[14px] text-[#1A1914]/60"><strong className="text-[#1A1914]">Best for:</strong> {f.bestFor}</p>
          </div>
          <div>
            <SectionLabel color="#E8590C">Key components</SectionLabel>
            <div className="grid sm:grid-cols-2 gap-3">
              {f.keyComponents.map((k) => (
                <div key={k} className="bg-white border border-[#E7E0D2] rounded-xl px-4 py-3.5 text-[14px] font-medium">{k}</div>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-[#12B76A]/5 border border-[#12B76A]/20 rounded-2xl p-5">
              <p className="font-mono-label text-[10px] text-[#0E9355] mb-3">Strengths</p>
              <ul className="space-y-2">{f.pros.map((p) => <li key={p} className="flex gap-2 text-[13.5px]"><Check size={15} className="text-[#0E9355] shrink-0 mt-0.5" />{p}</li>)}</ul>
            </div>
            <div className="bg-[#E5484D]/5 border border-[#E5484D]/20 rounded-2xl p-5">
              <p className="font-mono-label text-[10px] text-[#DC2626] mb-3">Watch-outs</p>
              <ul className="space-y-2">{f.cons.map((c) => <li key={c} className="flex gap-2 text-[13.5px]"><XIcon size={15} className="text-[#DC2626] shrink-0 mt-0.5" />{c}</li>)}</ul>
            </div>
          </div>
        </div>
        <aside className="space-y-4">
          <div className="bg-[#14130F] text-white rounded-2xl p-6">
            <p className="font-mono-label text-[10px] text-white/40">Not sure it fits?</p>
            <h3 className="mt-2 font-display font-semibold text-[20px]">Ask Sam to compare.</h3>
            <p className="mt-2 text-[13px] text-white/60">Open the chat (bottom-left) and ask “{f.name} vs an alternative for my industry”.</p>
          </div>
          {related.length > 0 && (
            <div className="bg-white border border-[#E7E0D2] rounded-2xl p-5">
              <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-3">Related in {f.category}</p>
              <ul className="space-y-2.5">
                {related.map((r) => <li key={r.slug}><Link to={`/library/${r.slug}`} className="text-[14px] font-medium text-[#3F4CE0] hover:underline">{r.name}</Link></li>)}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </main>
  );
}
